import React, { useRef, useEffect, useState } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import LoadingIndicator from "@default/general-components/loading-indicator";
import useMediaGalleryContext from "./../media-gallery/store/use-media-gallery-context";
import VideoSlide from "./components/video-slide";
import useAccessibilityContext from "@default/js/accessibility/store/use-accessibility-context";
import SwiperSimpleNavigation from "@default/paragraphs/media-gallery/swiper-simple-navigation";

const VideoSlider = ({ content }) => {
  const swiperRefLocal = useRef();
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const videoSliderRef = useRef();

  const {
    Swiper,
    SwiperSlide,
    Navigation,
    isCssLoaded,
    Autoplay,
    setSwiperRef,
    setActiveIndex,
    activeIndex,
  } = useMediaGalleryContext();

  const { simpleDesign } = useAccessibilityContext();

  const pauseAllVideos = () => {
    const allVideos =
      videoSliderRef?.current?.querySelectorAll("video, iframe");

    allVideos?.forEach((el) => {
      if (el.tagName.toLowerCase() === "video") {
        // Pause HTML5 video
        el.pause();
      } else if (el.tagName.toLowerCase() === "iframe") {
        // Check if it's a YouTube embed
        const src = el.getAttribute("src") || "";
        if (src.includes("youtube") || src.includes("youtu.be")) {
          el.contentWindow?.postMessage(
            JSON.stringify({
              event: "command",
              func: "pauseVideo",
              args: [],
            }),
            "*"
          );
        }
      }
    });
  };

  useEffect(() => {
    if (swiperRefLocal?.current && setSwiperRef) {
      setSwiperRef(swiperRefLocal);
    }
  }, [swiperRefLocal, setSwiperRef]);

  if (!Swiper || !SwiperSlide || !Navigation || !isCssLoaded) {
    return <LoadingIndicator fullPage={true} />;
  }

  return (
    <section
      className={classNames("paragraph paragraph-video-slider")}
      data-darkmode={content.fieldDarkMode}
      ref={videoSliderRef}
    >
      <div
        className={simpleDesign ? "container no-padding" : "container-fluid"}
      >
        <div className="col-12 position-relative">
          <SwiperSimpleNavigation
            prevRef={prevRef}
            nextRef={nextRef}
            centerSelector={".video-slide .video-wrapper"}
          />

          <Swiper
            spaceBetween={18}
            slidesPerView={2.2}
            loop={false}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            onBeforeInit={(swiper) => {
              // Assign navigation refs before Swiper initializes
              if (swiper.params.navigation) {
                swiper.params.navigation.prevEl = prevRef.current;
                swiper.params.navigation.nextEl = nextRef.current;
              }
            }}
            onRealIndexChange={(swiper) => {
              setActiveIndex(swiper.realIndex);
              pauseAllVideos();
            }}
            modules={[Navigation, Autoplay]}
            breakpoints={{
              0: { slidesPerView: simpleDesign ? 2 : 1 },
              768: { slidesPerView: simpleDesign ? 2 : 2.2 },
            }}
            ref={swiperRefLocal}
          >
            {content.fieldVideoSlides?.map((item, index) => (
              <SwiperSlide key={index} onClick={() => pauseAllVideos()}>
                {({ isActive }) => (
                  <VideoSlide content={item} active={isActive} />
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
};

VideoSlider.propTypes = {
  content: PropTypes.object.isRequired,
};

export default VideoSlider;
